import { yupResolver } from '~/helpers/yupResolver';
import { Editor } from '~/components/form/editor/Editor';
// import { Editor as Lexical } from '~/components/Editor';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faCog, faInfoCircle } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { EditorState, RawDraftContentState, convertToRaw } from 'draft-js';
import { memo, useEffect, useMemo, useRef } from 'react';
import {
  Control as BaseControl,
  Controller,
  UseFormSetValue,
  UseFormWatch,
  useForm,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { gql, useQuery } from 'urql';
import * as yup from 'yup';
import { Button } from '~/components/form/SubmitButton';
import { Input } from '~/components/form/TextField';
import { Select, SelectProps } from '~/components/form/downshift/Select';
import { useConfirm } from '~/components/ui/Confirm';
import {
  ASSET_SHARING_HELP_TEXT,
  mergeAssetSharingFields,
  resolveAssetSharingDefaults,
} from '~/constants/assetSharing';
import { IsCurrentUserAdminDocument, TaskInput } from '~/generated/graphql';
import { graphql } from '~/gql';
import { JobQuery } from '~/gql/graphql';
import { parseJson } from '~/helpers';
import { unique } from '~/helpers/array';
import { shouldResetCustomTaskForm } from '~/helpers/shouldResetCustomTaskForm';
import { useBreakpoint } from '~/hooks/useBreakpoint';
import { SideLayout } from '~/layouts/side/SideLayout';
import { useAssets } from '../AssetSelect';
import {
  AssetTypeSelect,
  buildAssetTypeOptions,
  useAssetTypes,
} from '../AssetTypeSelect';
import { TagEntityType } from '../TagCombobox';
import { useTags } from '../TagCombobox/tags';
import { AttributeSelect } from '../form/AttributeSelect';
import { Accordion } from '../ui/Accordion';
import { Switch } from '../ui/nucleus/Switch';
import { useTaskTypeOptions } from './CreateJobForm/ManageTasks';
import { DeleteTaskButton } from './DeleteTaskButton';
import { TaskIcon } from './JobView/TaskList/TaskListItem';
import { isTaskDefaultRule } from './assetDrivenTaskRules';
import { AssetTaskFields } from './TaskForm/AssetTaskFields';
import { UpdateTaskTags } from './tasks/UpdateTaskTags';

const AssetSharingDefaultsQuery = graphql(`
  query AssetSharingDefaults {
    assetSharingDefaults {
      shareAttributes
      shareJobHistory
      shareDeploymentHistory
    }
  }
`);

type FormValues = {
  name: string;
  description: RawDraftContentState | null | undefined;
  attributes: string[];
  jobAttributes: string[];
  tagIds: string[];
  type: string;
  required?: boolean;

  attribute?: {
    /** List of attribute IDs to include in the task */
    attributes: string[];
  };

  purchase?: {
    spaces: string[];
  };

  asset?: {
    assets: string[];
    selection: string;
    locationId: string;
    spaces: string[];
    assetTypes: string[];
    allow: string[];
    // Sharing permissions for external assignees
    shareAttributes: boolean;
    shareJobHistory: boolean;
    shareDeploymentHistory: boolean;
  };
};

type Props = {
  locationId?: string;
  task: TaskInput;
  taskType: string | null;
  updateTaskType: (id: string, value: string) => void;
  onApply: (task: TaskInput) => void;
  onClose: () => void;
  onDelete: (id: string) => void;
  scopeIds?: string[];
  showTaskOptions?: boolean;
  isJobTemplate?: boolean;
  canEditType?: boolean;
  onAttributesChange?: (taskId: string, attributes: string[]) => void;
  onAttributeTaskAttributesChange?: (
    taskId: string,
    attributeTaskAttributes: string[]
  ) => void;
  isAssetJob?: boolean;
  /** The job object for permission checks */
  job?: NonNullable<JobQuery['job']>;
  /** Custom title for the form (e.g., "Edit Rule" vs "Edit Task") */
  title?: string;
  /** Restrict task types to only these types (e.g., ['Asset'] for asset-based templates) */
  allowedTaskTypes?: string[];
  /** Disable specific asset selection, only allow asset type selection */
  disableSpecificAssetSelection?: boolean;
  /** Whether assignees are team members (null = no assignee, true = all team members, false = at least one external) */
  assigneeIsTeamMember?: boolean | null;
  /** Whether this task is being edited in an asset-driven template context */
  isAssetDrivenTemplate?: boolean;
  /** Whether this is a rule context (for asset-driven templates) - used for i18n-safe placeholder selection */
  isRuleContext?: boolean;
};

export enum AssetTaskSelection {
  SPECIFIC = 'specific',
  ALL = 'all',
}

export type Control = BaseControl<FormValues>;
export type Watch = UseFormWatch<FormValues>;
export type SetValue = UseFormSetValue<FormValues>;

const schema = yup
  .object({
    name: yup.string().required(),
  })
  .required();

// Stock features disabled: remove default creators (preserve existing values only)
/* function createDefaultValueRestock(): FormValues['item'] {
  return {
    /** Source location ID of the form Type:id */
/*     locationId: '',
    supplyJob: true,
    selectStock: SelectStockOption.MANUAL,
    // AUTO
    processQueuedChanges: false,
    fillTo: StockTransferRule.Target,
    // MANUAL
    reserveStock: true,
    dispatch: [],
    receive: [],
  };
}

function createDefaultValueStocktake(): FormValues['stocktake'] {
  return {
    include: Object.values(ItemType),
    itemStatus: [Status.Active],
    spaces: [],
  }; */

const CustomTaskFormComponent = function CustomTaskForm({
  locationId,
  task,
  taskType,
  updateTaskType,
  onApply,
  onClose,
  onDelete,
  scopeIds,
  isJobTemplate = false,
  canEditType = true,
  job,
  title,
  allowedTaskTypes,
  disableSpecificAssetSelection = false,
  assigneeIsTeamMember = null,
  isAssetDrivenTemplate = false,
  isRuleContext = false,
}: Props) {
  const { t } = useTranslation(['translation', 'job']);

  // Query session to check if current user is an administrator
  // This is more efficient than fetching all users via ListUsers
  const [{ data: adminData }] = useQuery({
    query: IsCurrentUserAdminDocument,
    requestPolicy: 'cache-first',
  });

  // Check if current user has admin role
  const isCurrentUserAdmin =
    adminData?.session?.user?.roles?.some(
      (role: { key: string }) => role.key === 'admin'
    ) ?? false;

  // Check if user has edit permissions - if so, they can configure sharing permissions
  // First check job.permissions (if job exists), then fall back to checking if current user is an administrator
  // This allows the sharing panel to appear for administrators even when job is undefined
  // (e.g., when editing job templates or in other non-job contexts)
  const hasJobEditPermission = Array.isArray(job?.permissions)
    ? job.permissions.includes('edit')
    : false;
  const shouldShowPermissions = hasJobEditPermission || isCurrentUserAdmin;

  // Memoized current task type for consistency across the component
  const currentTaskType = useMemo(
    () => task.type || taskType || '',
    [task.type, taskType]
  );

  // Fetch tenant defaults for asset sharing
  const [{ data: sharingDefaultsData }] = useQuery({
    query: AssetSharingDefaultsQuery,
    pause: currentTaskType !== 'Asset',
  });

  // Stock selection flows disabled
  // const [showSelect, setShowSelect] = useState<0 | 1 | null>(null);
  // const scope = [locationId];
  const multipleLocations = scopeIds && scopeIds.length > 1;

  const allTypeOptions = useTaskTypeOptions();
  // Filter type options if allowedTaskTypes is provided
  const typeOptions = useMemo(() => {
    if (allowedTaskTypes && allowedTaskTypes.length > 0) {
      return allTypeOptions.filter((option) =>
        allowedTaskTypes.includes(option.value)
      );
    }
    return allTypeOptions;
  }, [allTypeOptions, allowedTaskTypes]);

  const { siteId } = useParams();

  const editorState = EditorState.createEmpty();
  const defaultDescription = convertToRaw(editorState.getCurrentContent());

  const {
    control,
    formState: { isDirty, errors },
    handleSubmit,
    register,
    watch,
    setValue,
    reset,
  } = useForm<FormValues>({
    defaultValues: {},
    resolver: yupResolver(schema),
  });

  const { isMobile } = useBreakpoint();

  // Get sharing defaults from tenant settings or use system defaults
  const sharingDefaults = useMemo(
    () =>
      resolveAssetSharingDefaults(
        sharingDefaultsData?.assetSharingDefaults ?? null
      ),
    [sharingDefaultsData]
  );

  const previousTaskIdRef = useRef<string | undefined>(undefined);

  // Initialize form with task data
  useEffect(() => {
    const previousTaskId = previousTaskIdRef.current;
    previousTaskIdRef.current = task.id;
    // Don't reset if the user has already edited THIS task. Always reset
    // when the selected task changes, or Apply writes the previous task's
    // values onto the newly selected task.
    if (!shouldResetCustomTaskForm(isDirty, previousTaskId, task.id)) return;

    // CRITICAL: Only reset when task/taskType changes, NOT when sharingDefaults loads
    // The blink issue occurs because sharingDefaults loads async after initial render,
    // triggering an unnecessary form reset. We initialize the form once with system defaults,
    // then ignore subsequent sharingDefaults updates unless task itself changes.
    const formData = {
      name: task.name ?? '',
      description: task.description
        ? parseJson(task.description)
        : defaultDescription,
      attributes: task.attributes ?? [],
      jobAttributes: task.jobAttributes ?? [],
      type: currentTaskType,
      tagIds: task.tagIds ?? [],
      asset: {
        locationId: locationId ?? '',
        assets: task.asset?.assets ?? [],
        selection: task.asset?.selection
          ? task.asset.selection
          : multipleLocations || isJobTemplate
          ? AssetTaskSelection.ALL
          : AssetTaskSelection.SPECIFIC,
        spaces: task.asset?.spaces ?? [],
        // Preserve undefined for default rule tasks (don't convert to empty array)
        // Check if assetTypes property exists on task.asset - if it's explicitly undefined, preserve it
        // If it doesn't exist at all or is null, also use undefined
        // Convert null to undefined to match FormValues type
        assetTypes:
          task.asset && 'assetTypes' in task.asset
            ? task.asset.assetTypes ?? undefined // Convert null to undefined
            : undefined,
        allow: task.asset?.allow ?? [],
        // Use tenant defaults for sharing permissions, falling back to task values if they exist
        ...mergeAssetSharingFields(task.asset, sharingDefaults),
      },
      required: task.required ?? false,
      attribute: task.attribute
        ? { attributes: task.attribute.attributes ?? [] }
        : { attributes: [] },
      purchase: task.purchase
        ? { spaces: task.purchase.spaces ?? [] }
        : { spaces: [] },
    };

    reset(formData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task, taskType, currentTaskType, isDirty]);
  // Note: sharingDefaults intentionally excluded from deps to prevent reset when it loads async
  // Note: Also excluding reset, defaultDescription, multipleLocations, isJobTemplate, locationId
  // to prevent unnecessary resets. If form reset behavior is incorrect when these change, add them to deps.

  useConfirm({ when: isDirty });

  // Stock features disabled: skip updating Transfer Stock fields on job location changes
  // useEffect(() => {
  //   setValue('transfer.to.id', locationId ?? '');
  //   setValue('transfer.to.spaces', []);
  // }, [locationId, setValue]);

  // Watch for changes to attributes and update parent component
  const attributeTaskAttributes = watch('attribute.attributes');

  // Simplify the apply function since attributes are handled by callbacks
  const apply = (data: FormValues) => {
    const { name, description, asset, required } = data;
    const isRestock = currentTaskType === 'Restock';
    const isStocktake = currentTaskType === 'Stocktake';
    const isTransfer = currentTaskType === 'Transfer Stock';

    // Common sharing fields for asset tasks
    const sharingFields = mergeAssetSharingFields(asset, sharingDefaults);

    onApply({
      id: task.id,
      name,
      description: JSON.stringify(description),
      tagIds: data.tagIds,
      attributes: data.attributes,
      jobAttributes: data.jobAttributes,
      asset: asset
        ? {
            ...(asset.selection === AssetTaskSelection.SPECIFIC
              ? {
                  selection: asset.selection,
                  assets: asset.assets,
                  allow: asset.allow,
                }
              : {
                  selection: asset.selection,
                  assetTypes: asset.assetTypes,
                  spaces: asset.spaces,
                  allow: asset.allow,
                }),
            ...sharingFields,
          }
        : undefined,
      type: currentTaskType,
      // Stock features disabled: preserve existing values for stock tasks; do not send edited values
      item: isRestock ? task.item : undefined,
      attribute: data.attribute,
      purchase: data.purchase,
      stocktake: isStocktake ? task.stocktake : undefined,
      transfer: isTransfer ? task.transfer : undefined,
      required,
    });
  };

  // Stock features disabled: status options not used
  // const statusOptions = [
  //   { value: Status.Active, label: t('active') },
  //   { value: Status.Inactive, label: t('inactive') },
  // ];

  // const type = watch('type');
  // const selectStock = watch('item.selectStock');

  // Asset type selector for Setup section (asset-based templates only)
  const [assets] = useAssets();
  const [globalAssetTypes] = useAssetTypes();

  const selectedLocationAssetTypes = useMemo(() => {
    return unique(
      assets
        .filter((asset) => {
          const deployment = asset.deployment;
          if (scopeIds?.length) {
            return scopeIds?.includes(deployment?.location.id ?? '');
          }
          return asset;
        })
        .map((item) => item.type)
    );
  }, [assets, scopeIds]);

  const assetTypeOptions = useMemo(
    () =>
      buildAssetTypeOptions(
        globalAssetTypes,
        selectedLocationAssetTypes,
        isAssetDrivenTemplate
      ),
    [globalAssetTypes, selectedLocationAssetTypes, isAssetDrivenTemplate]
  );

  // Watch asset selection and types to detect default rule task
  const assetSelection = watch('asset.selection');
  const assetTypes = watch('asset.assetTypes');

  // Check if this is the default rule task (for asset-driven templates)
  const isDefaultRuleTask = useMemo(
    () =>
      isTaskDefaultRule(
        task,
        currentTaskType,
        isAssetDrivenTemplate,
        assetSelection,
        assetTypes
      ),
    [isAssetDrivenTemplate, currentTaskType, assetSelection, assetTypes, task]
  );

  function renderTaskOptions() {
    if (currentTaskType === 'Attribute') {
      return (
        <>
          <h3 className='mb-2 font-medium text-primary'>Attributes</h3>
          <p className='-mt-0.5 mb-2 text-sm text-secondary'>
            The attributes that will be audited for this task
          </p>
          <div className='transition-all duration-300 ease-in-out'>
            <AttributeSelect
              entityType='Site'
              value={attributeTaskAttributes}
              onChange={(selected) =>
                setValue('attribute.attributes', selected)
              }
              isAttributesTask
            />
          </div>
        </>
      );
    }

    if (currentTaskType === 'Asset') {
      return (
        <AssetTaskFields
          control={control}
          locationId={locationId}
          watch={watch}
          setValue={setValue}
          scopeIds={scopeIds}
          isJobTemplate={isJobTemplate}
          readOnlySpecificAssets={false}
          disableSpecificAssetSelection={disableSpecificAssetSelection}
          isAssetDrivenTemplate={isAssetDrivenTemplate}
        />
      );
    }

    return null;

    // Stock features disabled: Purchase form hidden
    /* if (taskType === 'Purchase') {
      return (
        <>
          <Controller
            name='purchase.spaces'
            control={control}
            render={({ field: { value, onChange } }) => (
              <SpacesSelect
                locationId={locationId}
                value={value ?? []}
                onChange={onChange}
                identifiedByName
                multiple
              />
            )}
          />
        </>
      );
    } */
    // Stock features disabled: Stocktake form hidden
    // if (taskType === 'Stocktake') {
    /* if (taskType === 'Stocktake') {
      return (
        <div className='mb-40'>
          <div className='my-4 text-sm'>Include:</div>
          {Object.values(ItemType).map((itemType) => {
            // Need a unique input name for each checkbox
            const fieldName = `stocktake.include.${itemType}`;
            return (
              <div key={itemType} className='mb-4 flex items-center'>
                <Switch
                  name={fieldName}
                  label={t(`itemType.${itemType}`, { count: 0 })}
                  checked={watch('stocktake.include').includes(itemType)}
                  onChange={(checked: boolean) => {
                    const currentValues = watch('stocktake.include');
                    if (checked && !currentValues.includes(itemType)) {
                      setValue('stocktake.include', [
                        ...currentValues,
                        itemType,
                      ]);
                    } else if (!checked && currentValues.includes(itemType)) {
                      setValue(
                        'stocktake.include',
                        currentValues.filter((item) => item !== itemType)
                      );
                    }
                  }}
                />
              </div>
            );
          })}

          <div className='my-4 text-sm'>Item Status:</div>
          {statusOptions.map(({ value, label }) => {
            // Need a unique input name for each checkbox
            const fieldName = `stocktake.itemStatus.${value}`;
            return (
              <div key={value} className='mb-4 flex items-center'>
                <Switch
                  name={fieldName}
                  checked={watch('stocktake.itemStatus').includes(value)}
                  onChange={(checked: boolean) => {
                    const currentValues = watch('stocktake.itemStatus');
                    if (checked && !currentValues.includes(value)) {
                      setValue('stocktake.itemStatus', [
                        ...currentValues,
                        value,
                      ]);
                    } else if (!checked && currentValues.includes(value)) {
                      setValue(
                        'stocktake.itemStatus',
                        currentValues.filter((item) => item !== value)
                      );
                    }
                  }}
                />
                <div
                  className={classNames(
                    'flex w-24 items-center justify-center rounded-full py-0.5 text-center text-white',
                    label === 'Active' ? 'bg-green-600' : 'bg-red-600'
                  )}
                >
                  {label}
                </div>
              </div>
            );
          })}

          <Controller
            name='stocktake.spaces'
            control={control}
            render={({ field: { value, onChange } }) => (
              <SpacesSelect
                locationId={locationId}
                value={value ?? []}
                onChange={onChange}
                multiple
              />
            )}
          />
        </div>
      );
    }

    // Old Move Stock/Stock Transfer version of Restock
    if (taskType === 'Restock') {
      return (
        <>
          <Controller
            name='item.locationId'
            control={control}
            render={({ field: { value, onChange } }) => (
              <SourceLocationSelect
                value={value ? [value] : []}
                onChange={(selected) => onChange(selected[0])}
              />
            )}
          />

          <div className='mb-4 flex items-center'>
            <Switch
              {...register('item.supplyJob')}
              name='item.supplyJob'
              type='field'
              checked={watch('item.supplyJob')}
              onChange={(checked: boolean) => {
                setValue('item.supplyJob', checked);
              }}
              label='Create job for source to supply the stock'
            />
            <HelperPopup>
              If you don't create a corresponding job for the stock to be
              supplied, the stock will be recorded as removed from the source at
              the same time it is added or used at the destination.
            </HelperPopup>
          </div>
          <div className='mb-4 flex items-center'>
            <p className='text-sm'>Select stock</p>
            <label className='block whitespace-nowrap pl-4 text-sm'>
              <input
                {...register('item.selectStock')}
                className='mr-2 align-text-top'
                type='radio'
                value={SelectStockOption.AUTO}
              />{' '}
              Automatically
            </label>
            <label className='block whitespace-nowrap pl-4 text-sm'>
              <input
                {...register('item.selectStock')}
                className='mr-2 align-text-top'
                type='radio'
                value={SelectStockOption.MANUAL}
              />{' '}
              Manually
            </label>
            <HelperPopup>
              Automatic stock selection uses the configured Target or Capacity
              of each SKU within its assigned Space and the available stock at
              the selected source to determine how much of each SKU should be
              selected.
            </HelperPopup>
          </div>
          {selectStock === SelectStockOption.AUTO ? (
            <>
              {/* If scope is site
          <Select
            label='Asset(s) to restock'
            options={[]}
            value={[]}
            onChange={() => {}}
          /> */
  }

  {
    /* <div className='mb-4'>
              <label className='text-brand text-xs font-semibold'>
                <input
                  className='mr-4 align-text-top'
                  type='checkbox'
                  name='processQueuedChanges'
                  checked={values.processQueuedChanges}
                  onChange={() => {}}
                  // onChange={handleChange}
                />
                Process queued changes
              </label>
            </div> */
  }

  {
    /* <div className='mb-4 flex'>
                <p className='text-sm'>Fill to</p>
                {Object.values(StockTransferRule).map((value) => (
                  <label key={value} className='block pl-4 text-sm'>
                    <input
                      {...register('item.fillTo')}
                      className='mr-2 align-text-top'
                      type='radio'
                      value={value}
                    />{' '}
                    {value}
                  </label>
                ))}
              </div>
            </>
          ) : (
            <div className='text-center'>
              <Button
                type='button'
                color='primary'
                onClick={() => setShowSelect(0)}
                disabled={!(locationId && watch('item.locationId'))}
              >
                Select Stock
              </Button>
              {!locationId ? (
                <p className='text-sm font-semibold text-delete'>
                  Job Location is required to select stock
                </p>
              ) : !watch('item.locationId') ? (
                <p className='text-sm font-semibold text-delete'>
                  Source Location is required to select stock
                </p>
              ) : null}
              {scope.length > 1 && (
                <p className='text-sm font-semibold text-copy-alt'>
                  Multiple job locations not yet supported.
                  <br />
                  Please select only one destination.
                </p>
              )}
            </div>
          )}
        </>
      );
    }
*/
  }
  /* if (taskType === 'Transfer Stock') {
      return (
        <>
          <Controller
            name='transfer.from.id'
            control={control}
            render={({ field: { value, onChange } }) => (
              <SourceLocationSelect
                value={value ? [value] : []}
                onChange={(selected) => onChange(selected[0])}
              />
            )}
          />
          <Controller
            name='transfer.from.spaces'
            control={control}
            render={({ field: { value, onChange } }) => (
              <SpacesSelect
                locationId={watch('transfer.from.id')}
                value={value ?? []}
                onChange={onChange}
              />
            )}
          />
          To:
          <Controller
            name='transfer.to.spaces'
            control={control}
            render={({ field: { value, onChange } }) => (
              <SpacesSelect
                locationId={locationId}
                value={value ?? []}
                onChange={onChange}
                multiple
              />
            )}
          />
        </>
      );
    }

    if (taskType === 'Asset') {
      return (
        <AssetTaskFields
          control={control}
          locationId={locationId}
          watch={watch}
          register={register}
          setValue={setValue}
          scopeIds={scopeIds}
          isJobTemplate={isJobTemplate}
        />
      );
    }

    return null;
  } */

  // Stock features disabled: selection flows hidden
  // if (showSelect === 0) {
  //   const sourceLocationId = watch('item.locationId');
  //   return (
  //     <StockToDispatch
  //       location={sourceLocationId}
  //       initialValues={{
  //         reserveStock: watch('item.reserveStock') ?? '',
  //         dispatch: watch('item.dispatch') ?? [],
  //       }}
  //       onBack={() => setShowSelect(null)}
  //       onSubmit={(form) => {
  //         setValue('item.reserveStock', form.reserveStock);
  //         setValue('item.dispatch', form.dispatch);
  //         setShowSelect(1);
  //       }}
  //     />
  //   );
  // }

  // if (showSelect === 1) {
  //   return (
  //     <StockToReceive
  //       location={scope[0]}
  //       dispatch={watch('item.dispatch')}
  //       initialValues={{ receive: watch('item.receive') ?? [] }}
  //       onBack={() => setShowSelect(0)}
  //       onSubmit={(values) => {
  //         setValue('item.receive', values.receive);
  //         setShowSelect(null);
  //       }}
  //     />
  //   );
  // }

  const [tagOptions] = useTags(TagEntityType.Task);

  return (
    <>
      <SideLayout
        className={classNames(
          siteId ? 'absolute inset-0' : 'absolute lg:flex-1 xl:relative'
        )}
        willOverflow={true}
      >
        <SideLayout.Head onClose={onClose} iconClass={'arrow-left'}>
          {title ?? 'Edit Task'}
        </SideLayout.Head>
        <SideLayout.Body className='pl-4 pr-3'>
          <Accordion mode='independent'>
            <Accordion.GreyCard className='relative z-[5]'>
              <Accordion.Button
                summary={
                  currentTaskType === 'Attribute'
                    ? 'Attribute Audit'
                    : currentTaskType
                }
              >
                <div className='flex items-center gap-2'>
                  <FontAwesomeIcon
                    className='h-4 w-4 text-secondary'
                    icon={faCog as IconProp}
                  />
                  <span>Set Up</span>
                </div>
              </Accordion.Button>
              <Accordion.Panel className='ease-standard transition-all'>
                <Accordion.PanelSection>
                  {typeOptions.length > 0 &&
                    (['Restock', 'Transfer Stock', 'Stocktake'].includes(
                      currentTaskType
                    ) || !canEditType ? (
                      <div className='mb-4 flex flex-col gap-1'>
                        <span className='text-sm font-medium text-grey-50'>
                          Type
                        </span>
                        <div className='flex gap-2 rounded-md border border-outline-bright bg-surface-container-low px-3 py-3 focus-visible:outline-none'>
                          <TaskIcon taskType={currentTaskType} />{' '}
                          {currentTaskType === 'Attribute'
                            ? 'Attribute Audit'
                            : currentTaskType}
                        </div>
                        <span className='text-sm text-secondary'>
                          {['Restock', 'Transfer Stock', 'Stocktake'].includes(
                            currentTaskType
                          )
                            ? 'Stock features are disabled at the moment. Existing tasks are preserved.'
                            : isDefaultRuleTask
                            ? 'Type cannot be changed for the default rule task'
                            : isJobTemplate
                            ? 'Complex Tasks Type cannot be changed after Template Creation'
                            : 'Complex Tasks Type cannot be changed after Job Creation'}
                        </span>
                      </div>
                    ) : (
                      <Select
                        floatingOptions={false}
                        label='Type'
                        options={typeOptions}
                        value={[currentTaskType]}
                        onChange={(selected) =>
                          updateTaskType(task.id, selected[0])
                        }
                        hasSelectorIcon
                      />
                    ))}

                  {/* Asset type selection for asset-based templates - shown in Setup section */}
                  {(() => {
                    return null;
                  })()}
                  {disableSpecificAssetSelection &&
                    currentTaskType === 'Asset' &&
                    (isDefaultRuleTask ? (
                      // For default rule task, show explanatory text instead of asset type selector
                      <div className='border-blue-200 bg-blue-50 mb-4 rounded-lg border p-4'>
                        <div className='mb-2 flex items-start gap-2'>
                          <div className='mt-0.5'>
                            <svg
                              className='text-blue-600 h-5 w-5'
                              fill='currentColor'
                              viewBox='0 0 20 20'
                            >
                              <path
                                fillRule='evenodd'
                                d='M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z'
                                clipRule='evenodd'
                              />
                            </svg>
                          </div>
                          <div className='flex-1'>
                            <h4 className='text-blue-900 font-medium'>
                              Default Task Rule
                            </h4>
                            <p className='text-blue-800 mt-1 text-sm'>
                              This task will automatically be used for any asset
                              with a type that doesn&apos;t match the types
                              specified in your other task rules. This ensures
                              all assets are covered even when you add new asset
                              types in the future.
                            </p>
                            <p className='text-blue-900 mt-2 text-sm font-medium'>
                              Targets: All asset types not covered by other
                              rules
                            </p>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className='mb-4'>
                        <Controller
                          name='asset.assetTypes'
                          control={control}
                          render={({ field: { value, onChange } }) => (
                            <AssetTypeSelect
                              value={value ?? []}
                              onChange={onChange}
                              options={assetTypeOptions}
                            />
                          )}
                        />
                      </div>
                    ))}

                  {/* Hide mandatory switch for asset-based templates */}
                  {!disableSpecificAssetSelection && (
                    <Controller
                      name='required'
                      control={control}
                      render={({ field }) => (
                        <Switch
                          name='required'
                          label='Mandatory'
                          help='Mandatory tasks are required to be completed prior to the job being marked as complete.'
                          checked={field.value ?? false}
                          onChange={(value: boolean) => field.onChange(value)}
                        />
                      )}
                    />
                  )}
                </Accordion.PanelSection>
              </Accordion.Panel>
            </Accordion.GreyCard>
            <Accordion.GreyCard error={false}>
              <Accordion.Button summary={task.name}>What</Accordion.Button>
              <Accordion.Panel>
                <Accordion.PanelSection title='Details'>
                  <Input
                    {...register('name')}
                    label={'Name *'}
                    className={classNames('mb-6', {
                      'border-red-500 focus:border-red-500': errors.name,
                    })}
                    error={errors.name?.message}
                    autoFocus={!isMobile}
                  />
                  <div className='mb-1 ml-1 mt-[12px] text-sm font-medium text-grey-50'>
                    {t('description')}
                  </div>
                  <Controller
                    name='description'
                    control={control}
                    render={({ field: { value, onChange } }) => (
                      <Editor
                        content={value}
                        onUpdate={onChange}
                        placeholder={
                          isJobTemplate && isRuleContext
                            ? t(
                                'jobTemplate.rules.descriptionPlaceholder',
                                'Add rule description...'
                              )
                            : t(
                                'jobTemplate.tasks.descriptionPlaceholder',
                                'Add task description...'
                              )
                        }
                      />
                    )}
                  />
                </Accordion.PanelSection>
                <Accordion.PanelSection title='Tags'>
                  <Controller
                    name='tagIds'
                    control={control}
                    render={({ field: { value, onChange } }) => (
                      <div>
                        <UpdateTaskTags
                          value={tagOptions.filter((t) =>
                            value?.includes(t.id)
                          )}
                          options={tagOptions}
                          onChange={onChange}
                        />
                      </div>
                    )}
                  />
                </Accordion.PanelSection>
                <Accordion.PanelSection heading='Job Location Attributes'>
                  <p className='-mt-1 mb-2 text-sm text-secondary'>
                    Information about the job location relevant to this task
                  </p>
                  <Controller
                    name='attributes'
                    control={control}
                    render={({ field }) => (
                      <AttributeSelect entityType='Site' {...field} />
                    )}
                  />
                </Accordion.PanelSection>
              </Accordion.Panel>
            </Accordion.GreyCard>
            {/* Hide assets section for rules-based templates (asset type selector is in Setup section) */}
            {currentTaskType !== 'Standard' &&
              !(
                currentTaskType === 'Asset' &&
                disableSpecificAssetSelection === true
              ) && (
                <Accordion.GreyCard className='z-10'>
                  <Accordion.Button>
                    <div className='flex items-center gap-2'>
                      <TaskIcon
                        className='text-secondary'
                        taskType={currentTaskType}
                      />
                      <span>
                        {currentTaskType === 'Attribute'
                          ? 'Attribute Audit'
                          : currentTaskType}
                      </span>
                    </div>
                  </Accordion.Button>
                  <Accordion.Panel>
                    <Accordion.PanelSection>
                      {renderTaskOptions()}
                    </Accordion.PanelSection>
                  </Accordion.Panel>
                </Accordion.GreyCard>
              )}
            {/* Share with External Assignee Section - only show for Asset tasks when user has permissions and there are external assignees */}
            {currentTaskType === 'Asset' &&
              shouldShowPermissions &&
              assigneeIsTeamMember === false && (
                <Accordion.GreyCard className='z-10'>
                  <Accordion.Button>Permission settings</Accordion.Button>
                  <Accordion.Panel>
                    <Accordion.PanelSection>
                      <p className='mb-4 text-sm text-secondary'>
                        Configure what external assignees can view:
                      </p>

                      <div className='flex flex-col gap-3'>
                        <Controller
                          name='asset.shareAttributes'
                          control={control}
                          render={({ field }) => (
                            <Switch
                              name='shareAttributes'
                              label='Attributes'
                              help={ASSET_SHARING_HELP_TEXT.shareAttributes}
                              checked={
                                field.value ?? sharingDefaults.shareAttributes
                              }
                              onChange={(checked: boolean) =>
                                field.onChange(checked)
                              }
                            />
                          )}
                        />

                        <Controller
                          name='asset.shareJobHistory'
                          control={control}
                          render={({ field }) => (
                            <Switch
                              name='shareJobHistory'
                              label='Job History'
                              help={ASSET_SHARING_HELP_TEXT.shareJobHistory}
                              checked={
                                field.value ?? sharingDefaults.shareJobHistory
                              }
                              onChange={(checked: boolean) =>
                                field.onChange(checked)
                              }
                            />
                          )}
                        />

                        <Controller
                          name='asset.shareDeploymentHistory'
                          control={control}
                          render={({ field }) => (
                            <Switch
                              name='shareDeploymentHistory'
                              label='Deployment History'
                              help={
                                ASSET_SHARING_HELP_TEXT.shareDeploymentHistory
                              }
                              checked={
                                field.value ??
                                sharingDefaults.shareDeploymentHistory
                              }
                              onChange={(checked: boolean) =>
                                field.onChange(checked)
                              }
                            />
                          )}
                        />
                      </div>

                      <div className='mt-3 flex items-start gap-2 text-xs text-tertiary'>
                        <FontAwesomeIcon
                          icon={faInfoCircle as IconProp}
                          className='mt-0.5 h-3.5 w-3.5 shrink-0'
                        />
                        <span>
                          Team members always have full access to asset details.
                        </span>
                      </div>
                    </Accordion.PanelSection>
                  </Accordion.Panel>
                </Accordion.GreyCard>
              )}
          </Accordion>

          <div className='mb-60 mt-20'>
            <DeleteTaskButton id={task.id} onDelete={onDelete} />
          </div>
        </SideLayout.Body>
        <SideLayout.Foot className='w-full p-4'>
          <Button onClick={handleSubmit(apply)}>{t('apply')}</Button>
        </SideLayout.Foot>
      </SideLayout>
    </>
  );
};

// Memoize CustomTaskForm to prevent unnecessary re-renders when parent form state changes
export const CustomTaskForm = memo(CustomTaskFormComponent);

type SpacesSelectProps = {
  locationId?: string;
  identifiedByName?: boolean;
} & Pick<SelectProps, 'value' | 'onChange' | 'multiple'>;

export function SpacesSelect({
  locationId,
  identifiedByName,
  value,
  onChange,
  ...props
}: SpacesSelectProps) {
  const { t } = useTranslation();

  // Use the same query as the working deployment form
  const [result] = useQuery<{
    location: { spaces: { id: string; name: string }[] };
  }>({
    query: gql`
      query ($locationId: ID!) {
        location(id: $locationId) {
          id
          spaces {
            id
            name
          }
        }
      }
    `,
    variables: { locationId: locationId ? `Site:${locationId}` : undefined },
    pause: !locationId,
  });

  const [all] = useQuery<{ spaceNames: string[] }>({
    requestPolicy: 'cache-and-network',
    query: '{ spaceNames }',
    pause: Boolean(locationId),
  });

  const spaceOptions =
    result.data?.location?.spaces?.map(({ id, name }) => ({
      value: identifiedByName ? name : id,
      label: name,
    })) ??
    all.data?.spaceNames?.map((value) => ({ value, label: value })) ??
    [];

  return (
    <Select
      {...props}
      label={t(props.multiple ? 'space_plural' : 'space')}
      options={spaceOptions}
      value={value || (props.multiple ? [] : '')}
      onChange={onChange}
    />
  );
}

// Stock features disabled: SourceLocationSelect not in use
/* function SourceLocationSelect(props: Pick<SelectProps, 'value' | 'onChange'>) {
  const [result] = useLocationsQuery({
    variables: {
      type: LocationType.Site,
    },
  });
  const { data } = result;

  return (
    <Select
      {...props}
      label='Source Location'
      options={
        data?.locations.map(({ __typename: type, id, name }) => ({
          value: `${type}:${id}`,
          label: name,
        })) ?? []
      }
    />
  );
} */
